import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { makeAutoObservable, reaction } from 'mobx';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import InitiateQuoteFlowDelegate from 'store/flows/initiate-quote/initiateQuoteFlowDelegate';
import { EffectiveDateConfigItem } from 'store/modules/initiate-quote/types';
import {
  DomainProductLineItem,
  DomainProductLinesDelegate,
} from 'store/modules/initiate-quote/domainProductLinesDelegate';

type DefaultBasicInfoFields = {
  proposalName: FormControl<string>;
  effectiveDate: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
  nhPublicSector?: FormControl<'Yes' | 'No'>;
};

export default class BasicInfoStepController {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private readonly _initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate;
  public readonly domainProductLinesDelegate: DomainProductLinesDelegate;
  public form: FormController<DefaultBasicInfoFields>;

  public get domainProductLineItems(): DomainProductLineItem[] {
    return this.domainProductLinesDelegate.domainProductLineItems;
  }

  public get isDomainProductLinesLoading(): boolean {
    return this.domainProductLinesDelegate.isLoading;
  }

  public get effectiveDatesConfig(): EffectiveDateConfigItem[] {
    return this._initiateQuoteFlowDelegate.effectiveDatesConfig;
  }

  public get isValid(): boolean {
    return (
      this.form.basicFieldsValidationsPassed &&
      !this.form.isLoading &&
      !this.domainProductLinesDelegate.isLoading &&
      this.domainProductLinesDelegate.hasQuotableProductLine
    );
  }

  private get _canFetchProductLines(): boolean {
    const {
      fields: { effectiveDate, zip, state, county },
    } = this.form;

    return !!(
      !effectiveDate.unpassedValidationMsg &&
      !zip.unpassedValidationMsg &&
      !state.unpassedValidationMsg &&
      !county.unpassedValidationMsg
    );
  }

  constructor(initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate) {
    this._initiateQuoteFlowDelegate = initiateQuoteFlowDelegate;
    this.domainProductLinesDelegate = this._initiateQuoteFlowDelegate.domainProductLinesDelegate;

    this.form = FormController.create(
      {
        proposalName: new FormControl<string>('', {
          validations: ['printableCharacters'],
        }),
        effectiveDate: new FormControl<string>(''),
      },
      {
        capabilities: [
          {
            type: 'zip',
            params: {
              zip: { optional: false },
              state: {
                customValidations: [
                  {
                    check: this._rootStore.domainStoreV2.isEligibleState,
                    msg: 'State is ineligible',
                  },
                ],
              },
            },
          },
        ],
      },
    );

    reaction(
      () => [
        this.form.fields.effectiveDate.value,
        this.form.fields.zip.value,
        this.form.fields.state.value,
        this.form.fields.county.value,
      ],
      async () => {
        if (!this._canFetchProductLines) {
          this.domainProductLinesDelegate.clearProductLines();
          return;
        }

        await this.domainProductLinesDelegate.getDomainProductLines(
          this.form.getFormDataObject() as Record<string, string>,
          true,
        );
      },
    );

    reaction(
      () => [this.form.fields.state.value],
      () => {
        if (this.form.fields.state.value === 'NH') {
          this.form.addFormField(
            'nhPublicSector',
            new FormControl<string>('', {
              optional: false,
              customValidations: [
                {
                  check: (value: 'Yes' | 'No'): boolean => value === 'No',
                  msg: 'Enrollment cannot be completed. Please contact your Northeast Delta Dental Sales executive',
                },
              ],
            }),
          );
        } else {
          this.form.removeFormField('nhPublicSector');
        }
      },
    );
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
